import { AlertTriangle, XClose } from "@untitledui/icons";
import { useId } from "react";
import { Heading } from "@/components/Atoms/Heading/Heading";
import { IconButton } from "@/components/Atoms/IconButton/IconButton";
import {
	Dialog,
	Modal,
	ModalOverlay,
} from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import type { ticketAdminFormPaths } from "@/features/form-editor/admin-form-editor-i18n-paths";
import { useAppTranslation } from "@/hooks/use-app-translation";

export interface FormEditorUnsavedChangesModalProps {
	isOpen: boolean;
	isApplying: boolean;
	isSaveInFlight?: boolean;
	paths: typeof ticketAdminFormPaths;
	onStay: () => void;
	onDiscard: () => void;
	onApply: () => void;
}

export function FormEditorUnsavedChangesModal({
	isOpen,
	isApplying,
	isSaveInFlight = false,
	paths,
	onStay,
	onDiscard,
	onApply,
}: FormEditorUnsavedChangesModalProps) {
	const { t } = useAppTranslation();
	const titleId = useId();
	const descriptionId = useId();
	const leaveActionsDisabled = isApplying || isSaveInFlight;

	return (
		<ModalOverlay
			isOpen={isOpen}
			isDismissable={false}
			isKeyboardDismissDisabled={leaveActionsDisabled}
			onOpenChange={(open) => {
				if (!open) onStay();
			}}
		>
			<Modal className="w-full sm:max-w-[500px]">
				<Dialog
					aria-labelledby={titleId}
					aria-describedby={descriptionId}
					className="outline-hidden"
				>
					<div className="relative flex w-full flex-col items-center rounded-2xl border border-primary bg-primary p-6 shadow-lg">
						<div className="absolute inset-x-0 top-0 z-10 flex items-start justify-end p-3">
							<IconButton
								aria-label={t("common.actions.close")}
								hierarchy="link"
								size="sm"
								shape="round"
								icon={(iconProps) => (
									<XClose {...iconProps} strokeWidth={2.5} />
								)}
								tooltip={t("common.actions.close")}
								isDisabled={leaveActionsDisabled}
								onPress={onStay}
								className="size-9! p-2! text-primary *:data-icon:text-primary"
							/>
						</div>

						<div className="flex w-full flex-col items-center gap-5 px-0 pt-2 pb-10">
							<FeaturedIcon
								icon={AlertTriangle}
								size="xl"
								color="error"
								theme="light"
							/>
							<div className="flex w-full flex-col items-center gap-1 text-center">
								<Heading id={titleId} level="h3" className="text-center">
									{t(paths.formEditor.unsaved.title)}
								</Heading>
								<div
									id={descriptionId}
									className="w-full text-center text-lg font-medium leading-7 text-secondary"
								>
									{t(paths.formEditor.unsaved.body)}
								</div>
							</div>
						</div>

						<div className="flex w-full flex-col-reverse gap-4 sm:flex-row sm:items-center">
							<div className="min-w-0 flex-1">
								<Button
									color="secondary"
									size="lg"
									className="w-full"
									onClick={onDiscard}
									isDisabled={leaveActionsDisabled}
								>
									{t(paths.formEditor.unsaved.discard)}
								</Button>
							</div>
							<div className="min-w-0 flex-1">
								<Button
									color="primary-destructive"
									size="lg"
									className="w-full"
									onClick={onApply}
									isLoading={isApplying}
									isDisabled={leaveActionsDisabled}
									showTextWhileLoading
								>
									{t(paths.formEditor.unsaved.saveAndContinue)}
								</Button>
							</div>
						</div>
					</div>
				</Dialog>
			</Modal>
		</ModalOverlay>
	);
}
